import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { find, get, omit } from 'lodash';
import { rd } from '@passionware/monads';

import { getTags, selectTagsStore } from 'store/v1/tags';
import { isValue, orderReducer } from 'v1/helpers/misc';
import { SearchField } from 'v1/components/shared';
import { useTags } from 'store/v1/tags/tags.hooks';
import { getGroupList } from '~/store/v1/groups/groups.selectors.js';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { myServices } from 'v5/services/services.connected.app';

const ResourceFieldFilters = ({
  className,
  resourceTypeId,
  query,
  filterList,
  onFilterChange
}) => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const resource_types = useSelector(state => state.resource_types);
  const resourceTypes = orderReducer(resource_types);
  const tagsStore = useSelector(selectTagsStore);
  const groupsStore = useSelector(getGroupList);
  const tagsList = useTags('resource'); //myTagsService here
  const queryFilters = get(query, 'filters');
  const customFields = get(auth, 'settings.custom_field_definitions');
  const selectedResourceType = resourceTypes.find(r => r.id === resourceTypeId);

  const scopeRd = myServices.appScopeService.useResolvedAppScope();
  const fieldsRequest = rd.tryMap(scopeRd, resolved =>
    filterList
      ? undefined
      : {
          type: 'resource',
          subtypeId: resourceTypeId,
          scope: appScopeUtils.identityFromResolved(resolved)
        }
  );
  const mergedFieldsRd =
    myServices.metastructureMappingService.useMetastructure(fieldsRequest);
  const mergedFields = rd.getOrElse(
    rd.useLastWithPlaceholder(mergedFieldsRd),
    null
  );

  const filterSource =
    filterList ||
    mergedFields ||
    get(selectedResourceType, ['metastructure', 'fields']) ||
    get(selectedResourceType, ['metastructure', 'field_order']);

  useEffect(() => {
    filterSource.find(f => f === 'tags' || f.name === 'tags') &&
      !tagsStore.fetched &&
      dispatch(getTags());
  }, []);

  const handleUpdate = (key, method, value, fieldType) => {
    let newQuery = {};
    // For BOOLEAN fields, null is a valid filter value (search for null/undefined)
    const hasValue =
      fieldType === 'BOOLEAN' ? value !== undefined : isValue(value);

    if (hasValue) {
      let filter;
      if (fieldType) {
        filter = {
          groups: get(queryFilters, 'filters.groups'),
          [method]: value,
          custom_field_definition_type: fieldType
        };
      } else {
        filter = { [method]: value };
      }
      newQuery = { filters: { ...queryFilters, [key]: filter } };
    } else {
      newQuery = { filters: omit(queryFilters, key) };
    }
    onFilterChange(newQuery);
  };

  const getCustomFieldObject = customFieldId => {
    const customFieldObject = find(customFields, c => c.id === customFieldId);
    if (customFieldObject && customFieldObject.filterable) {
      return {
        key: `custom_${customFieldObject.id}`,
        label: customFieldObject.name,
        data_type: customFieldObject.data_type,
        custom_field_definition_type: customFieldObject.data_type,
        custom_field_definition_object: customFieldObject,
        items: customFieldObject.options
      };
    }
    return false;
  };

  const getFilterList = () => {
    return filterSource.reduce((acc, keyValue) => {
      let customFieldId;

      if (typeof keyValue === 'string') {
        if (keyValue.startsWith('custom_')) {
          customFieldId = parseInt(keyValue.replace('custom_', ''));
        }
      } else {
        customFieldId = keyValue.custom_field_definition_id;
      }
      const key = typeof keyValue === 'string' ? keyValue : keyValue.name;

      let field;
      if (customFieldId) {
        field = getCustomFieldObject(customFieldId);
      } else {
        switch (key) {
          case 'locations':
            field = {
              key: 'locations',
              label: 'Location',
              data_type: 'SHORT_TEXT'
            };
            break;
          case 'tags':
            field = {
              key: 'tags',
              label: 'Tags',
              data_type: 'TAG_SELECT',
              tagSuggestions: tagsList
            };
            break;
          case 'groups':
            field = {
              key: 'groups',
              label: 'Groups',
              data_type: 'GROUP_SELECT'
            };
            break;
          case 'rates':
            // search for rates is broken anyway, let's skip it for now and work on it when we do v5 search experience
            return acc;
          // field = {
          //   key: 'rates',
          //   label: 'Rates',
          //   data_type: 'CURRENCY'
          // };
          // break;
          default:
            break;
        }
      }
      return field ? [...acc, field] : acc;
    }, []);
  };

  const renderField = (field, index) => {
    if (field) {
      if (field.custom_field_definition_type) {
        return (
          <SearchField
            key={`${field.key}-${index}`}
            field={field}
            fieldKey={field.key}
            onUpdate={handleUpdate}
            query={query}
            isCustom
          />
        );
      }
      return (
        <SearchField
          key={`${field.key}-${index}`}
          field={field}
          fieldKey={field.key}
          onUpdate={handleUpdate}
          query={query}
          tags={tagsStore}
          groups={groupsStore.filter(
            g => !g.archived && g.entity_id === resourceTypeId
          )}
        />
      );
    }
    return null;
  };

  const renderFields = () => {
    return getFilterList().map((field, index) => renderField(field, index));
  };

  return (
    <div className={classnames(['tailwind-root', className])}>
      {renderFields()}
    </div>
  );
};

ResourceFieldFilters.propTypes = {
  className: PropTypes.string,
  resourceTypeId: PropTypes.any,
  query: PropTypes.object.isRequired,
  filterList: PropTypes.arrayOf(PropTypes.string),
  onFilterChange: PropTypes.func // (query)
};

ResourceFieldFilters.props = {};

export default ResourceFieldFilters;
